'use client';

import { useState } from 'react';
import Link from 'next/link';
import { useGeo } from '@/components/GeoProvider';

/* ── Check / Cross icons ── */

function Check() {
  return (
    <svg className="w-4 h-4 shrink-0 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
    </svg>
  );
}

function Cross() {
  return (
    <svg className="w-4 h-4 shrink-0 text-stone-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

/* ── Types ── */

interface FeatureItem {
  name: string;
  standard: boolean;
  premium: boolean;
  elite: boolean;
}

interface FeatureGroup {
  title: string;
  features: FeatureItem[];
}

interface PlanDef {
  key: 'standard' | 'premium' | 'elite';
  label: string;
  description: string;
  priceMain: string;
  priceSub: string;
  accentColor: string;
  ctaTextColor?: string;
  highlighted?: boolean;
  cta: string;
  ctaLink: string;
  count: number;
}

/* ── Accent colors per plan key ── */
const ACCENT: Record<string, { color: string; ctaTextColor?: string }> = {
  standard: { color: 'var(--color-t-primary-light)' },
  premium: { color: 'var(--color-t-cta)', ctaTextColor: 'var(--color-t-cta-text)' },
  elite: { color: 'var(--color-t-primary)' },
};

/* ── Trust badge SVG paths ── */
const TRUST_ICONS: Record<string, string> = {
  'AES-256 Encrypted': 'M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z',
  'SOC 2 Compliant': 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
  '99.9% Uptime': 'M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  'No spam, ever': 'M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z',
};
const DEFAULT_TRUST_ICON = 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z'; // checkmark circle

/* ═══════════════════════════════════════════════════════════════
 * PRICING TABLE BLOCK RENDERER
 * ═══════════════════════════════════════════════════════════════ */

export function PricingTableBlockRenderer({ block }: { block: any }) {
  const { region, loading } = useGeo();
  const [annual, setAnnual] = useState(true);
  const [mobileTab, setMobileTab] = useState<'standard' | 'premium' | 'elite'>('premium');

  const sym = region.currencySymbol;
  const stdExtra = annual ? region.pricing.standardExtraYearly : region.pricing.standardExtra;
  const premPrice = annual ? region.pricing.premiumYearly : region.pricing.premium;
  const eliPrice = annual ? region.pricing.eliteYearly : region.pricing.elite;
  const savePct = Math.round((1 - region.pricing.premiumYearly / region.pricing.premium) * 100);

  /* Build feature groups from block data */
  const featureGroups: FeatureGroup[] = (block.featureGroups || []).map((g: any) => ({
    title: g.title,
    features: (g.features || []).map((f: any) => ({
      name: f.name,
      standard: !!f.standard,
      premium: !!f.premium,
      elite: !!f.elite,
    })),
  }));

  /* Feature counts per plan */
  const countFor = (key: string) =>
    featureGroups.reduce((sum, g) => sum + g.features.filter((f) => (f as any)[key]).length, 0);
  const standardCount = countFor('standard');
  const premiumCount = countFor('premium');
  const eliteCount = countFor('elite');

  /* Price helper */
  const priceFor = (key: string, isFree: boolean) => {
    if (isFree) return { main: 'FREE', sub: loading ? '…' : `${sym} ${stdExtra} / user / month from 6th user` };
    const priceMap: Record<string, number> = { premium: premPrice, elite: eliPrice };
    return { main: loading ? '' : `${sym}${priceMap[key] ?? 0}`, sub: 'User Per Month' };
  };

  const countMap: Record<string, number> = { standard: standardCount, premium: premiumCount, elite: eliteCount };

  /* Build plan defs from block data */
  const plans: PlanDef[] = (block.plans || []).map((p: any) => {
    const accent = ACCENT[p.key] || ACCENT.standard;
    const { main, sub } = priceFor(p.key, !!p.isFree);
    return {
      key: p.key,
      label: p.label,
      description: p.description || '',
      priceMain: main,
      priceSub: sub,
      accentColor: accent.color,
      ctaTextColor: accent.ctaTextColor,
      highlighted: !!p.highlighted,
      cta: p.ctaLabel || 'Start Free',
      ctaLink: p.ctaLink || '/setup-free-account',
      count: countMap[p.key] || 0,
    };
  });

  const activeMobilePlan = plans.find((p) => p.key === mobileTab) || plans[0];
  const trustBadges: { label: string }[] = block.trustBadges || [];

  return (
    <>
      {/* ─── Hero ─── */}
      <section
        className="relative pt-28 pb-16 overflow-hidden"
        style={{
          background:
            'var(--t-gradient, linear-gradient(135deg, var(--color-t-primary) 0%, var(--color-t-primary-dark) 100%))',
        }}
      >
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full opacity-10 bg-white" />
        <div className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full opacity-10 bg-white" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-3">
            {block.heading || 'Best prices to fit your needs'}
          </h1>
          <p className="text-base sm:text-lg text-white/70 max-w-2xl mx-auto mb-8">
            {block.subheading || 'Choose your plan. Cancel anytime.'}
          </p>

          {/* ── Billing toggle ── */}
          <div className="relative z-20 inline-flex items-center rounded-full bg-white/10 p-1 backdrop-blur-sm">
            <button
              onClick={() => setAnnual(false)}
              className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all ${
                !annual ? 'bg-white text-stone-900 shadow-md' : 'text-white/70 hover:text-white'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setAnnual(true)}
              className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all ${
                annual ? 'bg-white text-stone-900 shadow-md' : 'text-white/70 hover:text-white'
              }`}
            >
              Yearly
              {savePct > 0 && (
                <span
                  className="ml-1.5 text-xs font-bold"
                  style={annual ? { color: '#16a34a' } : { color: 'rgba(74,222,128,0.8)' }}
                >
                  Save {savePct}%
                </span>
              )}
            </button>
          </div>
        </div>
      </section>

      {/* ─── Pricing Cards ─── */}
      <section
        className="relative pt-8 pb-16 lg:pb-24"
        style={{
          background:
            'var(--t-gradient, linear-gradient(135deg, var(--color-t-primary) 0%, var(--color-t-primary-dark) 100%))',
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Mobile plan tabs */}
          <div className="flex lg:hidden justify-center mb-6 mt-6">
            <div className="inline-flex rounded-xl bg-white/10 p-1 backdrop-blur-sm">
              {plans.map((p) => (
                <button
                  key={p.key}
                  onClick={() => setMobileTab(p.key)}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                    mobileTab === p.key
                      ? 'bg-white text-stone-900 shadow-md'
                      : 'text-white/70 hover:text-white'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {/* Desktop: 3 cards side by side */}
          <div className="hidden lg:grid lg:grid-cols-3 gap-6 max-w-6xl mx-auto items-start">
            {plans.map((p) => (
              <PlanCard key={p.key} def={p} featureGroups={featureGroups} totalCount={eliteCount} loading={loading} />
            ))}
          </div>

          {/* Mobile: single active card */}
          <div className="lg:hidden max-w-md mx-auto">
            <PlanCard
              def={activeMobilePlan}
              featureGroups={featureGroups}
              totalCount={eliteCount}
              loading={loading}
            />
          </div>

          {/* Trust strip */}
          {trustBadges.length > 0 && (
            <div className="mt-16 flex flex-wrap items-center justify-center gap-6 text-white/50 text-xs">
              {trustBadges.map((b) => (
                <span key={b.label} className="flex items-center gap-1.5">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={TRUST_ICONS[b.label] || DEFAULT_TRUST_ICON} />
                  </svg>
                  {b.label}
                </span>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════
 * PLAN CARD — full feature list inside each card
 * ═══════════════════════════════════════════════════════════════ */

function PlanCard({
  def,
  featureGroups,
  totalCount,
  loading,
}: {
  def: PlanDef;
  featureGroups: FeatureGroup[];
  totalCount: number;
  loading: boolean;
}) {
  const { key, label, description, priceMain, priceSub, accentColor, ctaTextColor, highlighted, cta, ctaLink, count } = def;

  return (
    <div
      className={`flex flex-col rounded-2xl ${highlighted ? 'shadow-2xl z-10 border-t-4' : 'shadow-lg'}`}
      style={{
        backgroundColor: 'var(--color-t-surface)',
        borderColor: highlighted ? accentColor : undefined,
        borderTopColor: highlighted ? accentColor : undefined,
      }}
    >
      {/* Header */}
      <div
        className="px-7 pt-7 pb-5"
        style={{ borderBottom: '1px solid color-mix(in srgb, var(--color-t-primary) 12%, transparent)' }}
      >
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-2xl font-bold" style={{ color: highlighted ? accentColor : 'var(--color-t-heading)' }}>
            {label}
          </h2>
          {highlighted && (
            <span
              className="px-3 py-1 rounded-full text-[11px] font-bold text-white uppercase tracking-wide"
              style={{ backgroundColor: accentColor }}
            >
              Popular
            </span>
          )}
        </div>
        <p className="text-sm mb-5" style={{ color: 'var(--color-t-heading)', opacity: 0.7 }}>{description}</p>

        {/* Price */}
        <div className="mb-4">
          {loading && priceMain !== 'FREE' ? (
            <span
              className="inline-block w-28 h-14 rounded animate-pulse"
              style={{ backgroundColor: 'color-mix(in srgb, var(--color-t-primary) 10%, transparent)' }}
            />
          ) : (
            <span className="text-5xl font-extrabold tracking-tight" style={{ color: 'var(--color-t-heading)' }}>
              {priceMain}
            </span>
          )}
          <p className="text-sm mt-1 font-medium" style={{ color: 'var(--color-t-heading)', opacity: 0.6 }}>{priceSub}</p>
        </div>

        {/* Feature count bar */}
        <div className="mb-5">
          <span className="text-xs font-semibold" style={{ color: 'var(--color-t-heading)', opacity: 0.65 }}>
            {count} of {totalCount} features included
          </span>
          <div
            className="mt-2 h-2 rounded-full overflow-hidden"
            style={{ backgroundColor: 'color-mix(in srgb, var(--color-t-primary) 10%, transparent)' }}
          >
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{ width: `${(count / totalCount) * 100}%`, backgroundColor: accentColor }}
            />
          </div>
        </div>

        {/* CTA */}
        <Link
          href={ctaLink}
          className="block w-full py-3.5 rounded-xl text-center font-bold text-[15px] transition-all hover:opacity-90 hover:shadow-lg"
          style={{ backgroundColor: accentColor, color: ctaTextColor || '#FFFFFF' }}
        >
          {cta}
        </Link>
      </div>

      {/* Full feature list grouped by category */}
      <div className="flex-1 px-7 py-6 space-y-6">
        {featureGroups.map(({ title, features }) => {
          const hasAny = features.some((f) => f[key]);
          return (
            <div key={title}>
              <h3
                className="text-xs font-bold uppercase tracking-wider mb-3"
                style={{ color: hasAny ? accentColor : 'var(--color-t-heading)', opacity: hasAny ? 1 : 0.35 }}
              >
                {title}
              </h3>
              <ul className="space-y-2">
                {features.map((feat) => {
                  const included = feat[key];
                  return (
                    <li
                      key={feat.name}
                      className={`flex items-center gap-2.5 text-sm leading-normal ${included ? '' : 'opacity-30'}`}
                      style={{ color: 'var(--color-t-heading)' }}
                    >
                      {included ? <Check /> : <Cross />}
                      <span className={included ? 'font-medium' : 'line-through font-normal'}>{feat.name}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
}
